import { Section } from '@vnetwork/ui/layout';
import { StatusPill } from '@vnetwork/ui/data';
import type { VodJobStatuses } from '@/api/media/vod-jobs-types';
import styles from './JobDetailTimeline.module.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TimelineItem = any;

interface Props {
  timeline: TimelineItem[];
  statuses: VodJobStatuses[];
}

function stepTone(isFailed: boolean, isDone: boolean): 'danger' | 'success' | 'neutral' {
  if (isFailed) return 'danger';
  if (isDone) return 'success';
  return 'neutral';
}

const fmtTs = (raw?: string | number | null) => {
  if (!raw) return '—';
  return new Date(raw).toLocaleString();
};

export function JobDetailTimeline({ timeline, statuses }: Props) {
  const nameMap = (statuses ?? []).reduce<Record<number, string>>((acc, s) => {
    acc[Number(s.value)] = s.name;
    return acc;
  }, {});

  if (!timeline?.length) {
    return (
      <Section title="Pipeline Timeline" description="Job lifecycle status transitions." style={{ marginBottom: 0 }}>
        <p className={styles.empty}>No timeline data available.</p>
      </Section>
    );
  }

  return (
    <Section title="Pipeline Timeline" description="Job lifecycle status transitions.">
      <div className={styles.grid}>
        {timeline.map((item: TimelineItem, i: number) => {
          const code = Number(item?.status);
          const isFailed = Boolean(item?.error_code || item?.error_msg);
          const isLast = i === timeline.length - 1;
          const isDone = !isFailed && !isLast;
          const label = nameMap[code] || `Status ${code}`;

          const dotClass = isFailed
            ? styles.dotDanger
            : isDone
              ? styles.dotSuccess
              : styles.dotNeutral;

          return (
            <div key={`${code}-${i}`} className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={`${styles.dot} ${dotClass}`} />
                <span className={styles.stepLabel}>{label}</span>
              </div>
              <StatusPill tone={stepTone(isFailed, isDone)}>
                {isFailed ? 'Failed' : isDone ? 'Done' : 'Current'}
              </StatusPill>
              <p className={styles.stepDate}>{fmtTs(item?.created)}</p>
              {isFailed && item?.error_msg && (
                <p className={styles.stepError} title={item.error_msg}>{item.error_msg}</p>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}
